import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Users, Vote, Flag, ShieldAlert, TrendingUp, Loader2, Plus, Trash2, LogOut, LayoutDashboard, Home, FileText, Gavel, Copy, Handshake, Radio, Newspaper, Sparkles, Check, Send, Trophy, Radar, Flame } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#E6005C";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [judging, setJudging] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dir, setDir] = useState({ code: "", email: "", name: "" });
  const [inviteLink, setInviteLink] = useState("");
  const [crm, setCrm] = useState(null);
  const [standings, setStandings] = useState(null);
  const loadStandings = useCallback(() => api.get("/admin/rounds/standings").then((r) => setStandings(r.data)).catch(() => {}), []);
  const loadCrm = useCallback(() => api.get("/admin/crm/leads").then((r) => setCrm(r.data)).catch(() => {}), []);
  const [finance, setFinance] = useState(null);
  const [checkinCode, setCheckinCode] = useState("");
  const [checkinResult, setCheckinResult] = useState(null);
  const loadFinance = useCallback(() => api.get("/admin/finance").then((r) => setFinance(r.data)).catch(() => {}), []);

  // Livestream control
  const [live, setLive] = useState(null);
  const [liveForm, setLiveForm] = useState({ url: "", title: "", poll_question: "", poll_options: "" });
  const loadLive = useCallback(() => api.get("/livestream").then((r) => { setLive(r.data); setLiveForm((f) => ({ ...f, url: r.data.url || "", title: r.data.title || "", poll_question: r.data.poll?.question || "", poll_options: (r.data.poll?.options || []).join(", ") })); }).catch(() => {}), []);
  const saveLive = async (patch) => {
    try { const { data } = await api.put("/admin/livestream", patch); setLive(data); toast.success("Livestream updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const toggleLive = () => saveLive({ live: !live?.live, url: liveForm.url, title: liveForm.title });
  const savePoll = () => saveLive({ poll_question: liveForm.poll_question, poll_options: liveForm.poll_options.split(",").map((s) => s.trim()).filter(Boolean) });

  // Press accreditation
  const [pressEmail, setPressEmail] = useState("");
  const accreditPress = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/press/accredit", { email: pressEmail }); toast.success(`${pressEmail} accredited`); setPressEmail(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not accredit"); }
  };

  // AI Content Studio
  const [drafts, setDrafts] = useState([]);
  const [aiForm, setAiForm] = useState({ content_type: "Instagram caption", candidate_id: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const loadDrafts = useCallback(() => api.get("/admin/ai/drafts").then((r) => setDrafts(r.data)).catch(() => {}), []);
  const generateDraft = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    try { await api.post("/admin/ai/draft", { content_type: aiForm.content_type, candidate_id: aiForm.candidate_id || null }); toast.success("Draft generated"); loadDrafts(); }
    catch (e) { toast.error(e.response?.data?.detail || "AI generation failed"); }
    finally { setAiLoading(false); }
  };
  const setDraftStatus = async (id, status) => {
    await api.put(`/admin/ai/drafts/${id}`, { status });
    toast.success(status === "approved" ? "Approved" : "Updated");
    loadDrafts();
  };
  const publishDraft = async (id, channel) => {
    await api.post(`/admin/ai/drafts/${id}/publish`, channel ? { channel } : {});
    toast.success(channel === "Telegram" ? "Posted to Telegram" : "Published (Demo)");
    loadDrafts(); loadAutopilot();
  };
  const scheduleDraft = async (id) => {
    const when = window.prompt("Publish at (YYYY-MM-DD HH:MM, your local time). Leave blank for now:");
    if (when === null) return;
    const iso = when.trim() ? new Date(when.replace(" ", "T")).toISOString() : new Date().toISOString();
    try { await api.post(`/admin/ai/drafts/${id}/schedule`, { scheduled_at: iso, channel: "Demo" }); toast.success("Scheduled"); loadDrafts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };

  // AI Autopilot
  const [autopilot, setAutopilot] = useState(null);
  const loadAutopilot = useCallback(() => api.get("/admin/ai/autopilot").then((r) => setAutopilot(r.data)).catch(() => {}), []);
  const toggleAutopilot = async () => {
    const { data } = await api.put("/admin/ai/autopilot", { enabled: !autopilot?.enabled });
    setAutopilot((a) => ({ ...a, enabled: data.enabled }));
    toast.success(`Autopilot ${data.enabled ? "enabled" : "paused"}`);
  };
  const [runningPilot, setRunningPilot] = useState(false);
  const runAutopilot = async () => {
    setRunningPilot(true);
    try { const { data } = await api.post("/admin/ai/autopilot/run"); toast.success(`Autopilot ran — ${data.published} published, ${data.generated} drafted`); loadDrafts(); loadAutopilot(); }
    catch { toast.error("Autopilot run failed"); }
    finally { setRunningPilot(false); }
  };

  // AI Command Center — viral suggestions + social listening
  const [viralIdeas, setViralIdeas] = useState([]);
  const [viralTheme, setViralTheme] = useState("");
  const [viralLoading, setViralLoading] = useState(false);
  const [listening, setListening] = useState(null);
  const [listenLoading, setListenLoading] = useState(false);
  const loadAICommand = useCallback(() => {
    api.get("/admin/ai/viral").then((r) => setViralIdeas(r.data)).catch(() => {});
    api.get("/admin/ai/listening").then((r) => setListening(r.data)).catch(() => {});
  }, []);
  const generateViral = async () => {
    setViralLoading(true);
    try { const { data } = await api.post("/admin/ai/viral", { theme: viralTheme || null }); setViralIdeas((v) => [data, ...v]); toast.success("Viral ideas generated"); }
    catch { toast.error("AI generation failed"); }
    finally { setViralLoading(false); }
  };
  const scanListening = async () => {
    setListenLoading(true);
    try { const { data } = await api.post("/admin/ai/listening/scan"); setListening(data); toast.success("Social pulse updated"); }
    catch { toast.error("Scan failed"); }
    finally { setListenLoading(false); }
  };
  const doCheckin = async () => {
    try { const { data } = await api.post("/admin/tickets/checkin", { code: checkinCode }); setCheckinResult(data); if (data.ok) toast.success("Checked in!"); else toast.error("Already used"); setCheckinCode(""); }
    catch (e) { setCheckinResult({ ok: false, status: "not_found" }); toast.error(e.response?.data?.detail || "Ticket not found"); }
  };

  const inviteDirector = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/delegations/${dir.code}/invite-director`, { email: dir.email, name: dir.name });
      setInviteLink(data.invite_link || "");
      toast.success(data.emailed ? `Invitation emailed to ${dir.email}` : "Invite created — copy the link below");
      setDir({ code: "", email: "", name: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };

  const moveLead = async (lead, stage) => {
    await api.put(`/admin/crm/leads/${lead.id}`, { stage });
    toast.success(`Moved to ${stage}`);
    loadCrm();
  };
  const [crmEmail, setCrmEmail] = useState(null);
  const crmDraft = async (lead) => {
    setCrmEmail({ company: lead.company, loading: true });
    try {
      const kind = lead.stage === "inquiry" ? "outreach" : "follow_up";
      const { data } = await api.post(`/admin/crm/leads/${lead.id}/ai-draft`, { kind });
      setCrmEmail({ company: lead.company, subject: data.subject, body: data.body });
    } catch { toast.error("AI draft failed"); setCrmEmail(null); }
  };

  const advanceRound = async () => {
    if (!window.confirm("Advance to the next round? Lowest-ranked candidates will be eliminated.")) return;
    try { const { data } = await api.post("/admin/rounds/advance"); toast.success(`Advanced to ${data.current_round} — ${data.eliminated} eliminated`); loadStandings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot advance"); }
  };
  const resetRounds = async () => { await api.post("/admin/rounds/reset"); toast.success("Rounds reset"); loadStandings(); };
  const crownWinner = async (cid, name) => {
    if (!window.confirm(`Crown ${name} as the reigning titleholder?`)) return;
    try { await api.post("/admin/crown", { candidate_id: cid }); toast.success(`${name} crowned! 👑`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not crown"); }
  };
  const [form, setForm] = useState({ name: "", delegation_code: "", age: "", city: "", advocacy: "Education", bio: "", featured: false });
  const [saving, setSaving] = useState(false);

  // Fan Predictions admin
  const [predBoard, setPredBoard] = useState([]);
  const [predState, setPredState] = useState({ open: true, scored: false });
  const loadPredictions = useCallback(() => {
    api.get("/predictions/leaderboard").then((r) => setPredBoard(r.data)).catch(() => {});
    api.get("/predictions/state").then((r) => setPredState(r.data)).catch(() => {});
  }, []);
  const scorePredictions = async () => {
    if (!window.confirm("Lock predictions and score everyone against the current coronation Top 5? This closes new entries.")) return;
    try { const { data } = await api.post("/admin/predictions/score"); toast.success(`Scored ${data.scored} fans`); loadPredictions(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not score"); }
  };
  const togglePredictions = async () => {
    const { data } = await api.put("/admin/predictions/config", { open: !predState.open });
    setPredState((s) => ({ ...s, open: data.open }));
    toast.success(`Predictions ${data.open ? "opened" : "closed"}`);
  };

  const loadCandidates = useCallback(() => api.get("/admin/candidates").then((r) => setCandidates(r.data)).catch(() => {}), []);
  const loadApps = useCallback(() => api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin" && user.role !== "super_admin") { navigate("/account"); return; }
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/delegations").then((r) => setDelegations(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/judging/results").then((r) => setJudging(r.data)).catch(() => {});
    loadCandidates();
    loadApps();
  }, [user, loading, navigate, loadCandidates, loadApps]);

  if (loading || !user) return <div className="flex min-h-screen items-center justify-center bg-obsidian text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const addCandidate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/candidates", { ...form, age: form.age ? Number(form.age) : null });
      toast.success("Candidate added");
      setForm({ name: "", delegation_code: "", age: "", city: "", advocacy: "Education", bio: "", featured: false });
      loadCandidates();
      api.get("/admin/overview").then((r) => setOverview(r.data));
    } catch (e) { toast.error("Could not add", { description: e.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Deactivate ${c.name}?`)) return;
    await api.delete(`/admin/candidates/${c.id}`);
    toast.success("Candidate deactivated");
    loadCandidates();
  };

  const setAppStatus = async (app, status) => {
    await api.put(`/admin/applications/${app.id}/status`, { status });
    toast.success(`Marked ${status.replace("_", " ")}`);
    loadApps();
  };

  const togglePaidVoting = async () => {
    const { data } = await api.put("/admin/settings", { paid_voting_enabled: !settings?.paid_voting_enabled });
    setSettings(data);
    toast.success(`Paid voting ${data.paid_voting_enabled ? "enabled" : "disabled"}`);
  };

  const updateSetting = async (patch) => {
    const { data } = await api.put("/admin/settings", patch);
    setSettings(data);
    toast.success("Settings updated");
  };

  const assignDirector = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/delegations/${dir.code}/invite-director`, { email: dir.email, name: dir.name });
      toast.success(`Invitation emailed to ${dir.email}`);
      setDir({ code: "", email: "", name: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };

  const Metric = ({ Icon, label, value, accent }) => (
    <div className="card-lux p-5">
      <Icon className={`mb-2 h-5 w-5 ${accent || "text-gold"}`} />
      <p className="font-mono text-3xl gold-text tabular-nums">{Number(value ?? 0).toLocaleString()}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );

  return (
    <>
    <div className="min-h-screen bg-obsidian" data-testid="admin-page">
      <header className="glass-strong sticky top-0 z-40 border-b border-gold/20">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-2"><Crown className="h-6 w-6 text-gold" /><div><p className="font-serif text-sm text-amber-50 leading-none">Global Command Center</p><p className="font-mono text-[0.55rem] tracking-[0.25em] gold-text">MISS UNITED STATES OF AFRICA</p></div></div>
          <div className="flex items-center gap-2">
            <Link to="/" className="rounded-full border border-gold/25 p-2 text-gold hover:bg-gold/10"><Home className="h-4 w-4" /></Link>
            <button onClick={() => { logout(); navigate("/"); }} className="rounded-full border border-gold/25 px-4 py-2 text-sm text-gold hover:bg-gold/10 inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {[["overview", "Overview", LayoutDashboard], ["candidates", "Candidates", Users], ["applications", "Applications", FileText], ["judging", "Judging", Gavel], ["rounds", "Rounds", Crown], ["predictions", "Predictions", Trophy], ["crm", "Sponsors", Handshake], ["finance", "Finance", TrendingUp], ["livestream", "Livestream", Radio], ["press", "Press", Newspaper], ["ai", "AI Studio", Sparkles], ["aicommand", "AI Command", Radar]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => { setTab(id); if (id === "rounds") loadStandings(); if (id === "predictions") loadPredictions(); if (id === "crm") loadCrm(); if (id === "finance") loadFinance(); if (id === "livestream") loadLive(); if (id === "ai") { loadDrafts(); loadAutopilot(); } if (id === "aicommand") loadAICommand(); }} data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-display uppercase tracking-wider transition-all ${tab === id ? "gold-bg text-obsidian" : "border border-gold/25 text-gold/70"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
              <Metric Icon={Users} label="Candidates" value={overview?.candidates} />
              <Metric Icon={Flag} label="Delegations" value={overview?.delegations} />
              <Metric Icon={Vote} label="Total Votes" value={overview?.total_votes} />
              <Metric Icon={Crown} label="Registered Users" value={overview?.users} />
              <Metric Icon={ShieldAlert} label="Flagged Votes" value={overview?.flagged_votes} accent="text-red-400" />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="card-lux flex flex-wrap items-center justify-between gap-3 p-5" data-testid="paid-voting-toggle-row">
                <div><h3 className="text-lg text-amber-50">Paid Voting</h3><p className="text-xs text-muted-foreground">Enable purchasable vote packs (Stripe).</p></div>
                <button onClick={togglePaidVoting} data-testid="paid-voting-toggle"
                  className={`relative h-7 w-14 rounded-full transition-colors ${settings?.paid_voting_enabled ? "bg-emerald-accent" : "bg-obsidian-600"}`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${settings?.paid_voting_enabled ? "left-8" : "left-1"}`} />
                </button>
              </div>
              <div className="card-lux flex flex-wrap items-center justify-between gap-3 p-5" data-testid="require-verification-row">
                <div><h3 className="text-lg text-amber-50">Require Verification</h3><p className="text-xs text-muted-foreground">Only verified voters may cast votes.</p></div>
                <button onClick={() => updateSetting({ require_verification: !settings?.require_verification })} data-testid="require-verification-toggle"
                  className={`relative h-7 w-14 rounded-full transition-colors ${settings?.require_verification ? "bg-emerald-accent" : "bg-obsidian-600"}`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${settings?.require_verification ? "left-8" : "left-1"}`} />
                </button>
              </div>
              <div className="card-lux p-5" data-testid="judge-weight-row">
                <div className="mb-3 flex items-center justify-between"><h3 className="text-lg text-amber-50">Coronation Weighting</h3><span className="font-mono text-sm gold-text">{Math.round((settings?.judge_weight ?? 0.5) * 100)}% judges / {Math.round((1 - (settings?.judge_weight ?? 0.5)) * 100)}% votes</span></div>
                <input type="range" min="0" max="1" step="0.05" value={settings?.judge_weight ?? 0.5}
                  onChange={(e) => setSettings((s) => ({ ...s, judge_weight: Number(e.target.value) }))}
                  onMouseUp={(e) => updateSetting({ judge_weight: Number(e.target.value) })}
                  onTouchEnd={(e) => updateSetting({ judge_weight: Number(e.target.value) })}
                  data-testid="judge-weight-slider" className="w-full accent-[#E6005C]" />
              </div>
              <form onSubmit={inviteDirector} className="card-lux p-5 space-y-2" data-testid="assign-director-form">
                <h3 className="text-lg text-amber-50">Invite National Director</h3>
                <div className="grid grid-cols-2 gap-2">
                  <select required value={dir.code} onChange={(e) => setDir({ ...dir, code: e.target.value })} data-testid="assign-dir-code" className="rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-xs text-amber-50 outline-none">
                    <option value="">Delegation</option>{delegations.map((d) => <option key={d.code} value={d.code}>{d.flag} {d.name}</option>)}
                  </select>
                  <input required placeholder="Director name" value={dir.name} onChange={(e) => setDir({ ...dir, name: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-xs text-amber-50 outline-none" />
                </div>
                <input required type="email" placeholder="Director email" value={dir.email} onChange={(e) => setDir({ ...dir, email: e.target.value })} data-testid="assign-dir-email" className="w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-xs text-amber-50 outline-none" />
                <button className="btn-gold w-full text-xs py-2" data-testid="assign-dir-submit">Email Invite Link</button>
                {inviteLink && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-gold/25 bg-obsidian p-2" data-testid="invite-link-box">
                    <input readOnly value={inviteLink} className="flex-1 bg-transparent text-[0.65rem] text-amber-100/70 outline-none" onFocus={(e) => e.target.select()} id="invite-link-input" />
                    <button type="button" onClick={async () => {
                      try { await navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied"); }
                      catch { const el = document.getElementById("invite-link-input"); if (el) { el.select(); try { document.execCommand("copy"); toast.success("Invite link copied"); } catch { toast.info("Select the link and copy manually"); } } }
                    }} data-testid="copy-invite-link" className="rounded border border-gold/30 p-1.5 text-gold"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </form>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div className="card-lux p-6">
                <p className="kicker mb-6 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Votes by Region</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={overview?.regions || []}>
                    <XAxis dataKey="region" stroke="#94A3B8" fontSize={11} />
                    <YAxis stroke="#94A3B8" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#121217", border: "1px solid rgba(230,0,92,0.3)", borderRadius: 8, color: "#F8FAFC" }} cursor={{ fill: "rgba(230,0,92,0.08)" }} />
                    <Bar dataKey="votes" radius={[6, 6, 0, 0]}>
                      {(overview?.regions || []).map((_, i) => <Cell key={i} fill={GOLD} fillOpacity={1 - i * 0.13} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card-lux p-6">
                <p className="kicker mb-4">Top Candidates</p>
                <div className="divide-y divide-gold/10">
                  {(overview?.top_candidates || []).map((c, i) => (
                    <div key={c.slug} className="flex items-center gap-3 py-3">
                      <span className="font-mono w-5 text-muted-foreground">{i + 1}</span>
                      <span className="text-xl">{c.flag}</span>
                      <span className="flex-1 truncate text-sm text-amber-50">{c.name} <span className="text-muted-foreground">· {c.delegation_name}</span></span>
                      <span className="font-mono text-sm gold-text">{c.votes.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "candidates" && (
          <div data-testid="admin-candidates">
            <form onSubmit={addCandidate} className="card-lux mb-8 grid gap-3 p-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-add-form">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-cand-name" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold" />
              <select required value={form.delegation_code} onChange={(e) => setForm({ ...form, delegation_code: e.target.value })} data-testid="admin-cand-delegation" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold">
                <option value="">Select delegation</option>
                {delegations.map((d) => <option key={d.code} value={d.code}>{d.flag} {d.name}</option>)}
              </select>
              <input type="number" placeholder="Age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold" />
              <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold" />
              <select value={form.advocacy} onChange={(e) => setForm({ ...form, advocacy: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold">
                {["Education", "Climate", "Healthcare", "Youth Empowerment", "Technology", "Women's Leadership"].map((a) => <option key={a}>{a}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-amber-100/70"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#E6005C]" /> Featured</label>
              <input placeholder="Short bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold sm:col-span-2 lg:col-span-2" />
              <button disabled={saving} data-testid="admin-cand-submit" className="btn-gold inline-flex items-center justify-center gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Candidate</button>
            </form>

            <div className="card-lux overflow-hidden">
              <div className="divide-y divide-gold/10">
                {candidates.map((c) => (
                  <div key={c.id} className={`flex items-center gap-3 p-4 ${c.active ? "" : "opacity-40"}`} data-testid={`admin-cand-row-${c.slug}`}>
                    <img src={c.portrait_url} alt="" className="h-10 w-10 rounded-full object-cover object-top" />
                    <span className="flex-1 truncate text-sm text-amber-50">{c.name} {c.flag} <span className="text-muted-foreground">· {c.advocacy}</span></span>
                    <span className="hidden font-mono text-sm gold-text sm:block">{c.votes.toLocaleString()} votes</span>
                    {c.active && <button onClick={() => del(c)} data-testid={`admin-cand-delete-${c.slug}`} className="rounded-full border border-red-400/30 p-2 text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div data-testid="admin-applications">
            {applications.length === 0 ? (
              <div className="card-lux p-10 text-center text-amber-100/60">No applications submitted yet.</div>
            ) : (
              <div className="card-lux divide-y divide-gold/10">
                {applications.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-app-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-amber-50">{a.applicant_name || a.identity?.full_name || "Applicant"} <span className="text-muted-foreground">· {a.applicant_email}</span></p>
                      <p className="text-xs text-muted-foreground">Delegation: {a.delegation_code || "—"} · {a.photos?.length || 0} photos · {a.documents?.length || 0} docs</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs ${a.status === "approved" ? "bg-emerald-accent/20 text-emerald-accent" : a.status === "rejected" ? "bg-red-500/15 text-red-300" : "border border-gold/25 text-gold"}`}>{(a.status || "draft").replace("_", " ")}</span>
                    <select value={a.status || "draft"} onChange={(e) => setAppStatus(a, e.target.value)} data-testid={`admin-app-status-${a.id}`}
                      className="rounded-lg border border-gold/25 bg-obsidian px-3 py-1.5 text-xs text-amber-50 outline-none">
                      {["submitted", "under_review", "interview", "approved", "rejected", "waitlisted"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "judging" && (
          <div data-testid="admin-judging">
            <div className="mb-4 flex flex-wrap gap-2">
              {(judging?.categories || []).map((c) => (
                <span key={c.key} className="rounded-full border border-gold/25 px-3 py-1 text-xs text-gold/80">{c.name} <span className="text-muted-foreground">×{c.weight}</span></span>
              ))}
            </div>
            <div className="card-lux divide-y divide-gold/10">
              {(judging?.results || []).map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-4" data-testid={`judging-result-${r.id}`}>
                  <span className={`font-mono w-6 ${r.rank <= 3 ? "gold-text" : "text-muted-foreground"}`}>{r.rank}</span>
                  <span className="text-xl">{r.flag}</span>
                  <span className="flex-1 truncate text-sm text-amber-50">{r.name} <span className="text-muted-foreground">· {r.delegation_name}</span></span>
                  <span className="hidden text-xs text-muted-foreground sm:block">{r.judges_count} judge{r.judges_count === 1 ? "" : "s"}</span>
                  <span className="font-mono text-lg gold-text">{r.judge_score.toFixed(1)}</span>
                </div>
              ))}
              {(!judging || judging.results?.every((r) => r.judge_score === 0)) && (
                <p className="p-6 text-center text-sm text-muted-foreground">No judge scores submitted yet. Scores appear here as judges lock them in.</p>
              )}
            </div>
          </div>
        )}

        {tab === "rounds" && (
          <div data-testid="admin-rounds">
            <div className="mb-6 flex flex-wrap items-center gap-2">
              {(standings?.rounds || []).map((r, i) => (
                <span key={r.key} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs ${i === standings?.current_index ? "gold-bg text-obsidian" : i < (standings?.current_index ?? 0) ? "border border-emerald-accent/50 text-emerald-accent" : "border border-gold/20 text-amber-50/50"}`}>
                  {r.name}{r.size ? ` (${r.size})` : ""}
                </span>
              ))}
              <div className="ml-auto flex gap-2">
                <button onClick={resetRounds} className="rounded-full border border-gold/25 px-4 py-2 text-xs text-gold hover:bg-gold/10" data-testid="rounds-reset">Reset</button>
                <button onClick={advanceRound} className="btn-gold text-xs py-2" data-testid="rounds-advance">Advance to Next Round →</button>
              </div>
            </div>
            <p className="kicker mb-3">In Contention — Current Standings</p>
            <div className="card-lux divide-y divide-gold/10">
              {(standings?.in_contention || []).map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 p-4" data-testid={`standing-${c.id}`}>
                  <span className={`font-mono w-6 ${i < 3 ? "gold-text" : "text-muted-foreground"}`}>{i + 1}</span>
                  <img src={c.portrait_url} alt="" className="h-10 w-10 rounded-full object-cover object-top" />
                  <span className="flex-1 truncate text-sm text-amber-50">{c.name} {c.flag} <span className="text-muted-foreground">· {c.delegation_name}</span></span>
                  <span className="hidden font-mono text-xs text-muted-foreground sm:block">{c.votes.toLocaleString()} votes</span>
                  <span className="font-mono text-sm gold-text">{c.combined_score} pts</span>
                  {i === 0 && <button onClick={() => crownWinner(c.id, c.name)} className="rounded-full gold-bg px-3 py-1.5 text-xs text-obsidian font-bold inline-flex items-center gap-1" data-testid={`crown-${c.id}`}><Crown className="h-3 w-3" /> Crown</button>}
                </div>
              ))}
              {!standings?.in_contention?.length && <p className="p-6 text-center text-sm text-muted-foreground">Loading standings…</p>}
            </div>
          </div>
        )}

        {tab === "crm" && (
          <div data-testid="admin-crm">
            <p className="kicker mb-4">Sponsorship Pipeline · {crm?.total ?? 0} leads</p>
            <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
              {(crm?.stages || []).map((stage) => (
                <div key={stage} className="min-w-0">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-display uppercase tracking-wider text-gold">{stage}</span>
                    <span className="font-mono text-xs text-muted-foreground">{(crm.by_stage[stage] || []).length}</span>
                  </div>
                  <div className="space-y-2">
                    {(crm.by_stage[stage] || []).map((l) => (
                      <div key={l.id} className="card-lux p-3" data-testid={`crm-lead-${l.id}`}>
                        <p className="text-sm text-amber-50">{l.company}</p>
                        <p className="text-[0.65rem] text-muted-foreground">{l.package || "—"}</p>
                        {l.value > 0 && <p className="mt-1 font-mono text-xs gold-text">${Number(l.value).toLocaleString()}</p>}
                        <select value={l.stage} onChange={(e) => moveLead(l, e.target.value)} data-testid={`crm-stage-${l.id}`}
                          className="mt-2 w-full rounded border border-gold/20 bg-obsidian px-2 py-1 text-[0.65rem] text-amber-50 outline-none">
                          {crm.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => crmDraft(l)} data-testid={`crm-ai-draft-${l.id}`}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded border border-gold/30 px-2 py-1 text-[0.65rem] text-gold hover:bg-gold/10">
                          <Sparkles className="h-3 w-3" /> AI draft email
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "finance" && (
          <div data-testid="admin-finance">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="card-lux p-5"><p className="font-mono text-3xl gold-text">${Number(finance?.total ?? 0).toLocaleString()}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Revenue</p></div>
              <div className="card-lux p-5"><p className="font-mono text-3xl gold-text">{finance?.active_members ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Active Members</p></div>
              <div className="card-lux p-5"><p className="font-mono text-3xl gold-text">{finance?.tickets_sold ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Tickets Sold</p></div>
              <div className="card-lux p-5"><p className="font-mono text-3xl gold-text">{finance?.orders ?? 0}</p><p className="text-xs uppercase tracking-wider text-muted-foreground">Store Orders</p></div>
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="card-lux p-6">
                <p className="kicker mb-6">Revenue by Stream</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={finance?.streams || []} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                    <YAxis type="category" dataKey="stream" stroke="#94A3B8" fontSize={11} width={80} />
                    <Tooltip contentStyle={{ background: "#121217", border: "1px solid rgba(230,0,92,0.3)", borderRadius: 8, color: "#F8FAFC" }} cursor={{ fill: "rgba(230,0,92,0.08)" }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]}>{(finance?.streams || []).map((_, i) => <Cell key={i} fill={GOLD} fillOpacity={1 - i * 0.14} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card-lux p-6" data-testid="checkin-panel">
                <p className="kicker mb-4">Door Check-in</p>
                <p className="mb-3 text-sm text-amber-100/60">Scan a ticket QR or enter its code to validate entry.</p>
                <div className="flex gap-2">
                  <input value={checkinCode} onChange={(e) => setCheckinCode(e.target.value)} placeholder="MUSA-XXXXXXXX" data-testid="checkin-code" className="flex-1 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                  <button onClick={doCheckin} data-testid="checkin-submit" className="btn-gold text-xs">Validate</button>
                </div>
                {checkinResult && (
                  <div className={`mt-4 rounded-lg p-4 ${checkinResult.ok ? "bg-emerald-accent/15" : "bg-red-500/15"}`}>
                    <p className={`text-sm font-semibold ${checkinResult.ok ? "text-emerald-accent" : "text-red-300"}`}>
                      {checkinResult.status === "checked_in" ? "✓ Valid — Admitted" : checkinResult.status === "already_used" ? "Already used" : "Not found"}
                    </p>
                    {checkinResult.ticket && <p className="mt-1 text-xs text-amber-100/70">{checkinResult.ticket.event_name} · {checkinResult.ticket.tier}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "predictions" && (
          <div data-testid="admin-predictions">
            <div className="card-lux mb-6 flex flex-wrap items-center justify-between gap-4 p-6">
              <div>
                <p className="kicker mb-1 flex items-center gap-2"><Trophy className="h-4 w-4" /> Fan Predictions</p>
                <p className="text-sm text-amber-100/60">Score all fans against the current Coronation Top 5, or open/close new entries.</p>
                <p className="mt-1 text-xs text-muted-foreground">Status: {predState.scored ? "Scored & locked" : predState.open ? "Open for entries" : "Closed"}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={togglePredictions} disabled={predState.scored} data-testid="predictions-toggle" className="rounded-full border border-gold/25 px-4 py-2 text-xs text-gold hover:bg-gold/10 disabled:opacity-40">{predState.open ? "Close entries" : "Open entries"}</button>
                <button onClick={scorePredictions} disabled={predState.scored} data-testid="predictions-score" className="rounded-full gold-bg px-5 py-2 text-xs font-display uppercase tracking-wider text-obsidian disabled:opacity-40">Score & Reveal</button>
              </div>
            </div>
            <div className="card-lux divide-y divide-gold/10" data-testid="admin-prediction-leaderboard">
              {predBoard.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No predictions submitted yet.</p>}
              {predBoard.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-4">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-gold/25 text-xs font-bold text-gold">{i + 1}</span>
                  <span className="flex-1 text-sm text-amber-50">{r.name}</span>
                  {r.badge && <span className="rounded-full border border-gold/25 px-2 py-0.5 text-[0.6rem] text-gold">{r.badge}</span>}
                  <span className="font-mono text-sm gold-text">{r.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "livestream" && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-livestream">
            <div className="card-lux p-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="kicker">Broadcast Control</p>
                <span className={`rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest ${live?.live ? "bg-red-500/15 text-red-300" : "border border-gold/25 text-gold/70"}`}>{live?.live ? "● On Air" : "Offline"}</span>
              </div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Stream embed URL</label>
              <input value={liveForm.url} onChange={(e) => setLiveForm({ ...liveForm, url: e.target.value })} placeholder="https://www.youtube.com/embed/…" data-testid="live-url" className="mb-4 w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
              <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Broadcast title</label>
              <input value={liveForm.title} onChange={(e) => setLiveForm({ ...liveForm, title: e.target.value })} placeholder="Grand Coronation Finale" data-testid="live-title" className="mb-4 w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
              <button onClick={toggleLive} data-testid="live-toggle" className={`w-full rounded-full px-5 py-3 text-sm font-display uppercase tracking-wider ${live?.live ? "bg-red-500/20 text-red-300 border border-red-400/40" : "gold-bg text-obsidian"}`}>
                {live?.live ? "End Broadcast" : "Go Live"}
              </button>
            </div>
            <div className="card-lux p-6">
              <p className="kicker mb-4">Live Poll</p>
              <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Question</label>
              <input value={liveForm.poll_question} onChange={(e) => setLiveForm({ ...liveForm, poll_question: e.target.value })} placeholder="Who will win the crown?" data-testid="poll-question" className="mb-4 w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
              <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">Options (comma-separated)</label>
              <input value={liveForm.poll_options} onChange={(e) => setLiveForm({ ...liveForm, poll_options: e.target.value })} placeholder="Nigeria, Kenya, Egypt" data-testid="poll-options" className="mb-4 w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
              <button onClick={savePoll} data-testid="poll-save" className="btn-gold w-full">Publish Poll</button>
              {live?.poll?.question && <p className="mt-4 text-xs text-muted-foreground">Active: "{live.poll.question}" · {Object.values(live.poll.votes || {}).reduce((a, b) => a + (b || 0), 0)} votes</p>}
            </div>
          </div>
        )}

        {tab === "press" && (
          <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-press">
            <div className="card-lux p-6">
              <p className="kicker mb-4">Accredit a Journalist</p>
              <p className="mb-4 text-sm text-amber-100/60">Grant an existing account press access to the media kit. They must have registered first.</p>
              <form onSubmit={accreditPress} className="flex gap-2">
                <input required type="email" value={pressEmail} onChange={(e) => setPressEmail(e.target.value)} placeholder="journalist@outlet.com" data-testid="press-accredit-email" className="flex-1 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                <button data-testid="press-accredit-submit" className="btn-gold text-xs">Accredit</button>
              </form>
            </div>
            <div className="card-lux p-6">
              <p className="kicker mb-4">Accreditation Requests</p>
              <p className="text-sm text-amber-100/60">Incoming media requests appear in the <button onClick={() => { setTab("crm"); loadCrm(); }} className="text-gold hover:underline">Sponsors CRM</button> under "Media Accreditation" leads. Approve them there, then accredit the account email here.</p>
            </div>
          </div>
        )}

        {tab === "ai" && (
          <div data-testid="admin-ai">
            {/* Autopilot */}
            <div className="card-lux mb-6 p-6" data-testid="autopilot-panel">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="kicker mb-1 flex items-center gap-2"><Radio className="h-4 w-4" /> AI Autopilot</p>
                  <p className="text-sm text-amber-100/60">Auto-drafts a fresh caption daily for the trending candidate and publishes scheduled posts to your channels (Demo mode).</p>
                  <p className="mt-1 text-xs text-muted-foreground">{autopilot?.telegram_configured ? `✓ Telegram connected (${autopilot.telegram_channel})` : `Telegram not connected — posts log as Demo. Add a bot token to enable real posting to ${autopilot?.telegram_channel || "your channel"}.`}</p>
                  {autopilot?.last_run && <p className="mt-1 text-xs text-muted-foreground">Last run: {new Date(autopilot.last_run).toLocaleString()}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={runAutopilot} disabled={runningPilot} data-testid="autopilot-run" className="rounded-full border border-gold/25 px-4 py-2 text-xs text-gold hover:bg-gold/10 inline-flex items-center gap-2">{runningPilot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Run now</button>
                  <button onClick={toggleAutopilot} data-testid="autopilot-toggle" className={`rounded-full px-5 py-2 text-xs font-display uppercase tracking-wider ${autopilot?.enabled ? "gold-bg text-obsidian" : "border border-gold/25 text-gold/70"}`}>{autopilot?.enabled ? "On" : "Paused"}</button>
                </div>
              </div>
              {(autopilot?.log || []).length > 0 && (
                <div className="mt-5 max-h-40 space-y-1.5 overflow-y-auto border-t border-gold/10 pt-4" data-testid="autopilot-log">
                  {autopilot.log.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 text-xs">
                      <span className={`rounded-full px-2 py-0.5 font-mono uppercase ${l.action === "published" ? "bg-emerald-accent/20 text-emerald-accent" : "bg-gold/15 text-gold"}`}>{l.action}</span>
                      <span className="text-amber-100/70 truncate">{l.preview}{l.channel ? ` · ${l.channel}` : ""}</span>
                      <span className="ml-auto shrink-0 text-muted-foreground">{new Date(l.at).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card-lux p-6">
              <p className="kicker mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4" /> AI Content Studio</p>
              <form onSubmit={generateDraft} className="grid gap-3 sm:grid-cols-3">
                <select value={aiForm.content_type} onChange={(e) => setAiForm({ ...aiForm, content_type: e.target.value })} data-testid="ai-content-type" className="rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold">
                  {["Instagram caption", "X (Twitter) post", "Facebook post", "Press announcement", "Email newsletter blurb"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <select value={aiForm.candidate_id} onChange={(e) => setAiForm({ ...aiForm, candidate_id: e.target.value })} data-testid="ai-candidate" className="rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold">
                  <option value="">General / brand</option>
                  {candidates.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.delegation_name}</option>)}
                </select>
                <button disabled={aiLoading} data-testid="ai-generate" className="btn-gold inline-flex items-center justify-center gap-2">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate</button>
              </form>
            </div>
            <div className="mt-6 space-y-4">
              {drafts.length === 0 && <p className="text-sm text-muted-foreground">No drafts yet. Generate your first AI social post above.</p>}
              {drafts.map((d) => (
                <div key={d.id} className="card-lux p-5" data-testid={`ai-draft-${d.id}`}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <p className="text-[0.6rem] uppercase tracking-wider text-gold/70">{d.content_type}</p>
                    {d.autopilot && <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[0.55rem] uppercase tracking-wider text-gold">Autopilot</span>}
                    <span className={`ml-auto rounded-full px-3 py-1 text-xs ${d.status === "published" ? "bg-emerald-accent/25 text-emerald-accent" : d.status === "approved" || d.status === "scheduled" ? "bg-emerald-accent/15 text-emerald-accent" : "border border-gold/25 text-gold"}`}>{d.status}{d.status === "scheduled" && d.scheduled_at ? ` · ${new Date(d.scheduled_at).toLocaleString()}` : ""}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-amber-50/90">{d.text}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => { navigator.clipboard?.writeText(d.text); toast.success("Copied"); }} data-testid={`ai-copy-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-gold hover:bg-gold/10"><Copy className="h-3 w-3" /> Copy</button>
                    {d.status === "draft" && <button onClick={() => setDraftStatus(d.id, "approved")} data-testid={`ai-approve-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full gold-bg px-3 py-1.5 text-xs text-obsidian font-bold"><Check className="h-3 w-3" /> Approve</button>}
                    {(d.status === "approved" || d.status === "scheduled") && <button onClick={() => scheduleDraft(d.id)} data-testid={`ai-schedule-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-gold hover:bg-gold/10"><Radio className="h-3 w-3" /> Schedule</button>}
                    {(d.status === "approved" || d.status === "scheduled") && <button onClick={() => publishDraft(d.id, "Demo")} data-testid={`ai-publish-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-accent/20 px-3 py-1.5 text-xs text-emerald-accent font-bold"><Send className="h-3 w-3" /> Publish now</button>}
                    {(d.status === "approved" || d.status === "scheduled") && autopilot?.telegram_configured && <button onClick={() => publishDraft(d.id, "Telegram")} data-testid={`ai-telegram-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/20 px-3 py-1.5 text-xs text-sky-300 font-bold"><Send className="h-3 w-3" /> Post to Telegram</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "aicommand" && (
          <div className="space-y-8" data-testid="admin-aicommand">
            {/* Viral suggestions */}
            <div className="card-lux p-6" data-testid="viral-panel">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="kicker flex items-center gap-2"><Flame className="h-4 w-4" /> Viral Content Suggestions</p>
                  <p className="mt-1 text-sm text-amber-100/60">AI generates high-shareability content ideas tuned to your trending candidates.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input value={viralTheme} onChange={(e) => setViralTheme(e.target.value)} placeholder="Optional theme / campaign angle" data-testid="viral-theme" className="w-64 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                  <button onClick={generateViral} disabled={viralLoading} data-testid="viral-generate" className="btn-gold inline-flex items-center gap-2 text-xs">{viralLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate</button>
                </div>
              </div>
              {viralIdeas.length === 0 && <p className="text-sm text-muted-foreground">No ideas yet — generate your first viral batch.</p>}
              <div className="space-y-4">
                {viralIdeas.slice(0, 3).map((batch) => (
                  <div key={batch.id}>
                    <p className="mb-2 text-[0.6rem] uppercase tracking-wider text-gold/60">Theme: {batch.theme}</p>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {(batch.ideas || []).map((idea, i) => (
                        <div key={i} className="rounded-xl border border-gold/20 bg-obsidian p-4" data-testid="viral-idea">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[0.6rem] uppercase tracking-wider text-gold">{idea.format}</span>
                            <span className="font-mono text-xs text-gold">🔥 {idea.viral_score}</span>
                          </div>
                          <p className="font-serif text-base text-amber-50">{idea.hook}</p>
                          <p className="mt-2 text-xs text-amber-100/70 line-clamp-3">{idea.caption}</p>
                          <p className="mt-2 text-[0.6rem] text-rose/80">{(idea.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}</p>
                          <button onClick={() => { navigator.clipboard?.writeText(`${idea.hook}\n\n${idea.caption}\n\n${(idea.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}`); toast.success("Copied"); }} className="mt-3 inline-flex items-center gap-1.5 text-[0.65rem] text-gold hover:underline"><Copy className="h-3 w-3" /> Copy idea</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social listening */}
            <div className="card-lux p-6" data-testid="listening-panel">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="kicker flex items-center gap-2"><Radar className="h-4 w-4" /> AI Social Listening</p>
                  <p className="mt-1 text-sm text-amber-100/60">A live pulse of buzz, sentiment and trending topics across social platforms. <span className="text-gold/70">(AI-SIMULATED demo data)</span></p>
                </div>
                <button onClick={scanListening} disabled={listenLoading} data-testid="listening-scan" className="btn-gold inline-flex items-center gap-2 text-xs">{listenLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />} Scan now</button>
              </div>
              {!listening ? (
                <p className="text-sm text-muted-foreground">Run a scan to see the social pulse.</p>
              ) : (
                <div className="grid gap-6 lg:grid-cols-3">
                  <div className="space-y-4">
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Mentions today</p>
                      <p className="font-serif text-3xl gold-text">{(listening.volume || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="mb-2 text-xs text-muted-foreground">Sentiment</p>
                      <div className="flex h-3 overflow-hidden rounded-full">
                        <span className="bg-emerald-accent" style={{ width: `${listening.sentiment?.positive || 0}%` }} />
                        <span className="bg-gold/40" style={{ width: `${listening.sentiment?.neutral || 0}%` }} />
                        <span className="bg-rose" style={{ width: `${listening.sentiment?.negative || 0}%` }} />
                      </div>
                      <div className="mt-1.5 flex justify-between text-[0.6rem] text-muted-foreground">
                        <span className="text-emerald-accent">{listening.sentiment?.positive}% pos</span>
                        <span>{listening.sentiment?.neutral}% neu</span>
                        <span className="text-rose">{listening.sentiment?.negative}% neg</span>
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-xs text-muted-foreground">Trending topics</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(listening.trending_topics || []).map((t, i) => (
                          <span key={i} className="rounded-full border border-gold/25 px-2 py-1 text-[0.6rem] text-amber-50">{t.topic} <span className="text-gold">·{t.mentions}</span></span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <p className="mb-2 text-xs text-muted-foreground">Top mentions</p>
                    <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                      {(listening.mentions || []).map((m, i) => (
                        <div key={i} className="rounded-lg border border-gold/15 bg-obsidian p-3" data-testid="listening-mention">
                          <div className="flex items-center justify-between text-[0.6rem]">
                            <span className="uppercase tracking-wider text-gold/70">{m.platform} · {m.handle}</span>
                            <span className={m.sentiment === "positive" ? "text-emerald-accent" : m.sentiment === "negative" ? "text-rose" : "text-muted-foreground"}>{m.sentiment} · ♥ {m.likes}</span>
                          </div>
                          <p className="mt-1 text-sm text-amber-100/80">{m.text}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 rounded-lg border border-gold/20 bg-gold/5 p-3">
                      <p className="mb-1 text-[0.6rem] uppercase tracking-wider text-gold">Recommended actions</p>
                      <ul className="list-disc space-y-1 pl-4 text-xs text-amber-100/70">
                        {(listening.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    {crmEmail && (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/70 p-4 backdrop-blur-sm" onClick={() => setCrmEmail(null)} data-testid="crm-email-modal">
        <div className="w-full max-w-lg rounded-2xl glass-strong p-6" onClick={(e) => e.stopPropagation()}>
          <p className="kicker mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4" /> AI Outreach · {crmEmail.company}</p>
          {crmEmail.loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Drafting a personalized email…</div>
          ) : (
            <>
              <input readOnly value={crmEmail.subject} className="mb-3 w-full rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50" data-testid="crm-email-subject" />
              <textarea readOnly value={crmEmail.body} rows={11} className="w-full resize-none rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-100/90" data-testid="crm-email-body" />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setCrmEmail(null)} className="rounded-full border border-gold/25 px-4 py-2 text-xs text-amber-50/70 hover:bg-white/5">Close</button>
                <button onClick={() => { navigator.clipboard?.writeText(`Subject: ${crmEmail.subject}\n\n${crmEmail.body}`); toast.success("Email copied"); }} className="btn-gold inline-flex items-center gap-2 text-xs" data-testid="crm-email-copy"><Copy className="h-3.5 w-3.5" /> Copy email</button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}
